// composables/useConfirmationDialog.ts
import { ref, readonly } from 'vue'

interface ConfirmationDialogOptions {
  title: string
  text: string
  okColor?: string,
  icon?: string // e.g., 'i-heroicons-exclamation-triangle'
  confirmButtonText?: string
  cancelButtonText?: string
  onConfirm: () => void | Promise<void>
  onCancel?: () => void | Promise<void>
}

// --- State (kept private within the composable's scope) ---
const isOpen = ref(false)
isOpen.value = false
const title = ref('')
const text = ref('')
const icon = ref<string | undefined>(undefined)
const okColor = ref('warning')
const confirmButtonText = ref('OK')
const cancelButtonText = ref('Cancel')
const isConfirming = ref(false) // For loading state on confirm button

let resolvePromise: (value: boolean) => void
let currentOnConfirm: (() => void | Promise<void>) | null = null
let currentOnCancel: (() => void | Promise<void>) | null = null
// --- End State ---

export function useConfirmationDialog() {
  const _showDialog = (options: ConfirmationDialogOptions): Promise<boolean> => {
    title.value = options.title
    text.value = options.text
    icon.value = options.icon
    if (options.okColor){
      okColor.value = options.okColor
    }
    confirmButtonText.value = options.confirmButtonText || 'OK'
    cancelButtonText.value = options.cancelButtonText || 'Cancel'
    currentOnConfirm = options.onConfirm
    currentOnCancel = options.onCancel || null
    isOpen.value = true
    isConfirming.value = false // Reset loading state

    return new Promise<boolean>((resolve) => {
      resolvePromise = resolve
    })
  }

  const handleConfirm = async () => {
    if (currentOnConfirm) {
      isConfirming.value = true
      try {
        await currentOnConfirm()
        resolvePromise(true)
      } catch (error) {
        console.error("Error during onConfirm:", error)
        // Decide if an error in onConfirm should count as 'not confirmed'
        // For now, it still resolves true as the dialog was "confirmed",
        // but the action itself might have failed.
        // Alternatively, resolvePromise(false) or re-throw.
        resolvePromise(true) // Or false, depending on desired behavior for errors in actionFn
      } finally {
        isConfirming.value = false
        isOpen.value = false
      }
    } else {
      resolvePromise(true)
      isOpen.value = false
    }
    resetState()
  }

  const handleCancel = async () => {
    if (currentOnCancel) {
      try {
        await currentOnCancel()
      } catch (error) {
        console.error("Error during onCancel:", error)
      }
    }
    resolvePromise(false)
    isOpen.value = false
    resetState()
  }

  const resetState = () => {
    currentOnConfirm = null
    currentOnCancel = null
  }

  /**
   * Shows a confirmation dialog with detailed options.
   */
  const showConfirmation = (options: ConfirmationDialogOptions): Promise<boolean> => {
    return _showDialog(options)
  }

  /**
   * Shows a confirmation dialog and executes a given function with arguments if confirmed.
   * @param dialogTitle Title of the confirmation dialog.
   * @param dialogText Text/message in the confirmation dialog.
   * @param dialogIcon Optional icon for the dialog (e.g., 'i-heroicons-trash').
   * @param actionFn The function to execute if the user confirms.
   * @param actionArgs Arguments to pass to the actionFn.
   * @returns Promise<boolean> Resolves true if confirmed, false otherwise.
   */
  const confirmAndExecute = async (
    dialogTitle: string,
    dialogText: string,
    okColor: string | undefined,
    dialogIcon: string | undefined, // Use undefined for no icon
    actionFn: (...args: any[]) => void | Promise<void>,
    ...actionArgs: any[]
  ): Promise<void> => {
    _showDialog({
      title: dialogTitle,
      text: dialogText,
      okColor: okColor,
      icon: dialogIcon,
      onConfirm: async () => {
        await actionFn(...actionArgs)
      },
      // onCancel is handled by _showDialog resolving false by default
    })
  }


    const confirmAndExecute_old = async (
    dialogTitle: string,
    dialogText: string,
    dialogIcon: string | undefined, // Use undefined for no icon
    actionFn: (...args: any[]) => void | Promise<void>,
    ...actionArgs: any[]
  ): Promise<boolean> => {
    return _showDialog({
      title: dialogTitle,
      text: dialogText,
      icon: dialogIcon,
      onConfirm: async () => {
        await actionFn(...actionArgs)
      },
      // onCancel is handled by _showDialog resolving false by default
    })
  }

  return {
    // State for the component to bind to
    isOpen: readonly(isOpen),
    title: readonly(title),
    text: readonly(text),
    okColor: readonly(okColor),
    icon: readonly(icon),
    confirmButtonText: readonly(confirmButtonText),
    cancelButtonText: readonly(cancelButtonText),
    isConfirming: readonly(isConfirming),

    // Methods for the component to call (intended for ConfirmationDialog.vue)
    _handleConfirm: handleConfirm,
    _handleCancel: handleCancel,

    // Public methods for pages/components to call
    showConfirmation,    // The original, more flexible method
    confirmAndExecute,   // The new convenience method
  }
}